import React, { Suspense, useMemo } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { useAtomValue } from "jotai/react";

import { useTonWallet } from "@arc/sdk";
import { PageLoader } from "@arc/ui/loader";

import { onboardingCompletedAtom } from "@/atoms/user";
import {
  AppRoute,
  AssetsRoute,
  BankRoute,
  CatalogRoute,
  DepositRoute,
  MarketRoute,
  SettingsRoute,
} from "@/constants/routes";
import { usePlatformFeature } from "@/hooks/platform";
import { useStartParamsParser } from "@/utils/hooks/useStartParamsParser";
import { Layout } from "../Layout";
import { InitTcConnectionsListener } from "./InitTcConnectionsListener";
import { InitTcTransactionsRequestsListener } from "./InitTcTransactionsRequestsListener";
import { InitWalletMigration } from "./InitWalletMigraton";

// prettier-ignore
const appRoutes = {
  [AppRoute.welcome]: React.lazy(() => import("@/pages/Welcome").then((module) => ({ default: module.Welcome }))),
  [AppRoute.import]: React.lazy(() => import("@/pages/Import").then((module) => ({ default: module.ImportRoutes }))),
  [AppRoute.wallet]: React.lazy(() => import("@/pages/Wallet").then((module) => ({ default: module.Wallet }))),
  [AppRoute.swap]: React.lazy(() => import("@/pages/Swap").then((module) => ({ default: module.Swap }))),
  [AppRoute.send]: React.lazy(() => import("@/pages/Send").then((module) => ({ default: module.SendRoutes }))),
  [AppRoute.history]: React.lazy(() => import("@/pages/Wallet").then((module) => ({ default: module.History }))),
  [AppRoute.assets]: React.lazy(() => import("@/pages/Wallet").then((module) => ({ default: module.Assets }))),
  [AppRoute.nfts]: React.lazy(() => import("@/pages/Wallet").then((module) => ({ default: module.Nfts }))),

  [BankRoute.index]: React.lazy(() => import("@/pages/Bank").then((module) => ({ default: module.Bank }))),
  [BankRoute.tasks]: React.lazy(() => import("@/pages/Bank").then((module) => ({ default: module.BankTasks }))),
  [BankRoute.stake]: React.lazy(() => import("@/pages/Bank").then((module) => ({ default: module.BankStaking }))),
  [BankRoute.buy]: React.lazy(() => import("@/pages/Bank").then((module) => ({ default: module.BankBuy }))),
  [BankRoute.history]: React.lazy(() => import("@/pages/Bank").then((module) => ({ default: module.BankHistory }))),
  [BankRoute.referrals]: React.lazy(() => import("@/pages/Bank").then((module) => ({ default: module.BankReferrals }))),

  [DepositRoute.index]: React.lazy(() => import("@/pages/Deposit").then((module) => ({ default: module.Deposit }))),
  [DepositRoute.external]: React.lazy(() => import("@/pages/Deposit").then((module) => ({ default: module.DepositExternal })),),

  [CatalogRoute.index]: React.lazy(() => import("@/pages/Catalog").then((module) => ({ default: module.Catalog }))),
  [CatalogRoute.detail]: React.lazy(() => import("@/pages/Catalog").then((module) => ({ default: module.CatalogDetail }))),
  [CatalogRoute.detailSlug]: React.lazy(() => import("@/pages/Catalog").then((module) => ({ default: module.CatalogDetail }))),
  [CatalogRoute.leaders]: React.lazy(() => import("@/pages/Catalog").then((module) => ({ default: module.CatalogLeaders })),),
  [CatalogRoute.category]: React.lazy(() => import("@/pages/Catalog").then((module) => ({ default: module.CatalogCategory })),),
  [CatalogRoute.taskInfo]: React.lazy(() => import("@/pages/Catalog").then((module) => ({ default: module.TaskInfo })),),
  [CatalogRoute.taskList]: React.lazy(() => import("@/pages/Catalog").then((module) => ({ default: module.TaskList })),),
  [CatalogRoute.taskCreate]: React.lazy(() => import("@/pages/Catalog").then((module) => ({ default: module.TaskCreate })),),
  [CatalogRoute.taskVerification]: React.lazy(() => import("@/pages/Catalog").then((module) => ({ default: module.TaskVerification })),),
  [CatalogRoute.taskVerificationReview]: React.lazy(() => import("@/pages/Catalog").then((module) => ({ default: module.TaskVerification })),),
  [CatalogRoute.taskHistory]: React.lazy(() => import("@/pages/Catalog").then((module) => ({ default: module.TaskHistory })),),
  [CatalogRoute.taskConfirmTransactions]: React.lazy(() => import("@/pages/Catalog").then((module) => ({ default: module.TaskConfirmTransactions }))),
  [MarketRoute.index]: React.lazy(() => import("@/pages/Market").then((module) => ({ default: module.Market }))),
  [MarketRoute.list]: React.lazy(() => import("@/pages/Market").then((module) => ({ default: module.OrdersList }))),
  [MarketRoute.order]: React.lazy(() => import("@/pages/Market").then((module) => ({ default: module.SharedOrder }))),
  [MarketRoute.create]: React.lazy(() => import("@/pages/Market").then((module) => ({ default: module.CreateOrder }))),
  [MarketRoute.confirm]: React.lazy(() => import("@/pages/Market").then((module) => ({ default: module.ConfirmCreateOrder }))),
  [MarketRoute.myOrders]: React.lazy(() => import("@/pages/Market").then((module) => ({ default: module.MyOrders }))),

  [AppRoute.congratulations]: React.lazy(() => import("@/pages/Stars/Congratulations").then((module) => ({ default: module.Congratulations }))),

  [AppRoute.news]: React.lazy(() => import("@/pages/News").then((module) => ({ default: module.News }))),
  [SettingsRoute.index]: React.lazy(() => import("@/pages/Settings").then((module) => ({ default: module.Settings }))),
  [SettingsRoute.walletSafety]: React.lazy(() => import("@/pages/Settings").then((module) => ({ default: module.WalletSafety }))),
  [SettingsRoute.walletLanguage]: React.lazy(() => import("@/pages/Settings").then((module) => ({ default: module.WalletLanguage }))),
  [SettingsRoute.connectedApps]: React.lazy(() => import("@/pages/Settings").then((module) => ({ default: module.ConnectedApps }))),

  [AssetsRoute.nft]: React.lazy(() => import("@/pages/Wallet").then((module) => ({ default: module.NftDetail }))),
};
// prettier-ignore-end

export const MainRoutes = () => {
  const lp = usePlatformFeature();

  const onboardingCompleted = useAtomValue(onboardingCompletedAtom);
  const activeWallet = useTonWallet();

  useStartParamsParser(lp.useTelegramData());

  const startPath = useMemo(() => {
    if (!onboardingCompleted) return AppRoute.welcome;
    if (!activeWallet) return AppRoute.catalog;

    return AppRoute.wallet;
  }, [onboardingCompleted, activeWallet]);

  return (
    <>
      <Routes>
        <Route element={<Layout />}>
          {Object.entries(appRoutes).map(([path, Component]) => (
            <Route
              key={path}
              element={
                <Suspense fallback={<PageLoader />}>
                  <Component />
                </Suspense>
              }
              path={path}
            />
          ))}
          <Route element={<Navigate to={startPath} />} path="*" />
        </Route>
      </Routes>

      {activeWallet && <InitTcConnectionsListener />}
      {activeWallet && <InitTcTransactionsRequestsListener />}
      <InitWalletMigration />
    </>
  );
};
